/**
 * Proper "Sign in with Bluesky" — atproto OAuth via @atproto/oauth-client-browser.
 * Redirect-based: the user authenticates on THEIR PDS, we never see a password.
 * The library handles PKCE/DPoP/refresh and stores tokens in the browser. Scope
 * (see /client-metadata.json) is identity + write to ONLY our own collection —
 * no posting, no follows.
 *
 * Reads of our profile record are done unauthenticated (atproto records are
 * public), so they never depend on the OAuth scope; only writes use the session.
 */

import {
  BrowserOAuthClient,
  buildLoopbackClientId,
  atprotoLoopbackClientMetadata,
} from "@atproto/oauth-client-browser";
import { Agent } from "@atproto/api";
import type { OAuthSession } from "@atproto/oauth-client";
import { log } from "../engine/debug";

const HANDLE_RESOLVER = "https://bsky.social";
const PLC = "https://plc.directory";

/**
 * OAuth scope: identity + write access to ONLY our own collections. Must match
 * public/client-metadata.json (the production client). Sessions created before
 * a scope change keep their old grants until the user signs in again.
 */
export const OAUTH_SCOPE =
  "atproto repo:com.equalnotequals.tanks.profile repo:com.equalnotequals.tanks.gamestate";

let client: BrowserOAuthClient | null = null;

function isLoopback(): boolean {
  const h = location.hostname;
  return h === "localhost" || h === "127.0.0.1" || h === "[::1]";
}

async function getClient(): Promise<BrowserOAuthClient> {
  if (client) return client;
  if (isLoopback()) {
    // dev: loopback client (works on 127.0.0.1 without hosted metadata). The
    // DEFAULT loopback metadata only carries the bare "atproto" scope — no repo
    // write grant — so every putRecord 403'd and saves silently failed. Encode
    // the real scope into the loopback client id instead.
    const clientId = `${buildLoopbackClientId(window.location)}&scope=${encodeURIComponent(OAUTH_SCOPE)}`;
    client = new BrowserOAuthClient({
      handleResolver: HANDLE_RESOLVER,
      clientMetadata: atprotoLoopbackClientMetadata(clientId),
    });
  } else {
    client = await BrowserOAuthClient.load({
      clientId: `${location.origin}/client-metadata.json`,
      handleResolver: HANDLE_RESOLVER,
    });
  }
  return client;
}

/** The scope the active client will request (exposed for debugging/tests). */
export async function clientScope(): Promise<string | undefined> {
  return (await getClient()).clientMetadata.scope;
}

export interface Session {
  did: string;
  handle: string;
  agent: Agent;
  oauth: OAuthSession;
  /** the scope the auth server actually GRANTED (may lag the requested scope) */
  grantedScope: string | null;
}

/** Does this session's token carry write access to our collections? */
export function canWrite(s: Session): boolean {
  // unknown scope (older lib/server) -> assume yes and let the write surface errors
  if (!s.grantedScope) return true;
  return s.grantedScope.includes("repo:com.equalnotequals.tanks");
}

async function toSession(s: OAuthSession): Promise<Session> {
  const did = s.did;
  let handle = did;
  let grantedScope: string | null = null;
  try {
    const r = await fetch(`https://public.api.bsky.app/xrpc/app.bsky.actor.getProfile?actor=${encodeURIComponent(did)}`).then((x) => x.json());
    if (r?.handle) handle = r.handle;
  } catch {
    /* keep did as fallback */
  }
  try {
    grantedScope = (await s.getTokenInfo()).scope ?? null;
    log.info("atproto", `granted scope: ${grantedScope ?? "(unknown)"}`);
  } catch {
    /* scope stays unknown */
  }
  return { did, handle, agent: new Agent(s), oauth: s, grantedScope };
}

/** On page load: restore an existing session OR process an OAuth callback. */
export async function initAuth(): Promise<Session | null> {
  try {
    const c = await getClient();
    const result = await c.init();
    if (!result?.session) return null;
    // strip the ?code=…&state=… callback params from the URL bar
    if (location.search) history.replaceState(null, "", location.pathname);
    return await toSession(result.session);
  } catch {
    return null;
  }
}

/** Start sign-in — REDIRECTS the page to the user's PDS auth server. */
export async function signIn(handle: string): Promise<void> {
  const c = await getClient();
  await c.signIn(handle.trim().replace(/^@/, ""));
}

export async function signOut(s: Session): Promise<void> {
  try {
    await s.oauth.signOut();
  } catch {
    /* ignore */
  }
}

// ---- PDS record helpers --------------------------------------------------

async function pdsForDid(did: string): Promise<string> {
  let doc: { service?: Array<{ id?: string; type?: string; serviceEndpoint?: string }> };
  if (did.startsWith("did:plc:")) doc = await fetch(`${PLC}/${did}`).then((r) => r.json());
  else if (did.startsWith("did:web:")) doc = await fetch(`https://${did.slice(8)}/.well-known/did.json`).then((r) => r.json());
  else throw new Error("unsupported did");
  const svc = (doc.service ?? []).find((s) => s.id?.endsWith("#atproto_pds") || s.type === "AtprotoPersonalDataServer");
  if (!svc?.serviceEndpoint) throw new Error("no pds");
  return svc.serviceEndpoint.replace(/\/$/, "");
}

/** Read one of the user's records — UNAUTHENTICATED (records are public). */
export async function readRecord<T>(did: string, collection: string, rkey: string): Promise<T | null> {
  try {
    const pds = await pdsForDid(did);
    const url = `${pds}/xrpc/com.atproto.repo.getRecord?repo=${encodeURIComponent(did)}&collection=${encodeURIComponent(collection)}&rkey=${encodeURIComponent(rkey)}`;
    const r = await fetch(url);
    if (!r.ok) return null;
    const d = (await r.json()) as { value: T };
    return d.value;
  } catch {
    return null;
  }
}

/**
 * The atproto data model has NO floats — any non-integer number anywhere in a
 * record makes the PDS reject the whole write. Fields that need precision must
 * be scaled to integers by the caller; this pass rounds anything that slips
 * through so a stray float can never brick saving again.
 */
function intsOnly(v: unknown): unknown {
  if (typeof v === "number") {
    if (Number.isInteger(v)) return v;
    log.warn("atproto", `non-integer ${v} in record — rounded (atproto has no floats; scale it instead)`);
    return Math.round(v);
  }
  if (Array.isArray(v)) return v.map(intsOnly);
  if (v && typeof v === "object") {
    return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, intsOnly(x)]));
  }
  return v;
}

/** Write one record into the user's own repo (OAuth, scoped to our collection). */
export async function writeRecord(s: Session, collection: string, rkey: string, record: Record<string, unknown>): Promise<void> {
  try {
    await s.agent.com.atproto.repo.putRecord({
      repo: s.did,
      collection,
      rkey,
      record: intsOnly({ $type: collection, ...record }) as Record<string, unknown>,
      // our lexicons aren't published — never let the PDS try to validate them
      validate: false,
    });
  } catch (e) {
    // surface WHY (the old silent-swallow hid this failing for months)
    log.warn("atproto", `putRecord ${collection}/${rkey} failed`, String(e));
    throw e;
  }
}

/** Delete one of the user's records (used to expire saved game state). */
export async function deleteRecord(s: Session, collection: string, rkey: string): Promise<void> {
  try {
    await s.agent.com.atproto.repo.deleteRecord({ repo: s.did, collection, rkey });
  } catch (e) {
    log.warn("atproto", `deleteRecord ${collection}/${rkey} failed`, String(e));
    throw e;
  }
}
